import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Heart, Leaf, Users, Phone, Mail, MapPin, Calendar, ArrowRight, Sparkles, Shield, BookOpen, MessageCircle, Brain, Target, Bed, Apple, Flower2, Home, Globe, Coffee, Zap, Activity } from 'lucide-react';
import NavBar from '@/components/NavBar';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import heroImage from '@/assets/hero_image.jpg';

const HeadacheMigrainePage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Headache & Migraine Relief Currimundi | Chinese Medicine";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Natural headache and migraine relief with Chinese Medicine in Currimundi. Treat tension headaches, migraines & chronic pain. Book online or visit our Sunshine Coast clinic.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Headache & Migraine Relief Currimundi | Chinese Medicine");
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Natural headache and migraine relief with Chinese Medicine in Currimundi. Treat tension headaches, migraines & chronic pain. Book online or visit our Sunshine Coast clinic.");
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/headache-migraine");
    }
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/hero_image.jpg");
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Headache & Migraine Relief Currimundi | Chinese Medicine");
    }
    const schema = {
      "@context": "https://schema.org",
      "@type": "MedicalClinic",
      "name": "Women and The Way - Headache & Migraine Relief with Chinese Medicine",
      "image": "https://womenandtheway.com.au/hero_image.jpg",
      "description": "Natural headache and migraine relief with Chinese Medicine in Currimundi. Treat tension headaches, migraines & chronic pain.",
      "url": "https://womenandtheway.com.au/headache-migraine",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "postalCode": "4551",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "telephone": "0415537784",
      "email": "womenandtheway@gmail.com",
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      },
      "serviceType": "Headache Relief, Migraine Treatment, Pain Management, Chinese Medicine, Acupuncture, Herbal Medicine",
      "areaServed": {
        "@type": "Place",
        "name": "Sunshine Coast, Queensland, Australia"
      },
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  Headache & Migraine Relief
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Soften the tension. Clear the fog. Find relief that lasts.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Headaches and migraines can significantly impact your quality of life. At Women and the Way, we address the root causes of these conditions, not just the symptoms, using Chinese Medicine to restore balance and reduce frequency and intensity.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Whether you're experiencing tension headaches, migraines, or hormonal headaches, our treatments help identify and address the underlying imbalances. Our approach combines the wisdom of Traditional Chinese Medicine with modern understanding of headache physiology. Many of our clients also benefit from our <a href="/stress-burnout" className="text-sage-600 hover:text-sage-800 underline transition-colors">stress relief treatments</a> and <a href="/sleep-support" className="text-sage-600 hover:text-sage-800 underline transition-colors">sleep support services</a>.
                </p>
                <Button 
                  size="lg" 
                  className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  {isLoading ? 'Booking...' : 'Book Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={heroImage} 
                  alt="Headache and migraine relief with Chinese medicine on the Sunshine Coast" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* The Chinese Medicine View on Headaches */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Brain className="w-8 h-8 text-sage-600" />
                The Chinese Medicine View on Headaches
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                In Traditional Chinese Medicine (TCM), headaches are not all the same. Their location, sensation, frequency, and triggers tell us which systems are out of balance. Whether it's Liver Yang rising, Qi or Blood deficiency, external Wind invasion, or emotional tension, we tailor your care to the pattern behind the pain.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                At Women and the Way, we use acupuncture, Chinese herbal medicine, and lifestyle strategies to address the root cause of recurring headaches, helping you experience lasting relief and regain control over your wellbeing.
              </p>
            </div>
          </div>
        </section>

        {/* What We Treat */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Treat
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Tension Headaches</h3>
                  </div>
                  <p className="text-sage-700">Often related to Liver Qi stagnation, stress, or neck/shoulder tension</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Migraines with aura, visual disturbance, or nausea</h3>
                  </div>
                  <p className="text-sage-700">Liver Yang rising, Phlegm accumulation, or internal Wind</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Sharp, stabbing, one-sided pain</h3>
                  </div>
                  <p className="text-sage-700">Blood stasis or Liver Heat</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Coffee className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Dull, aching, or fatigue-linked headaches</h3>
                  </div>
                  <p className="text-sage-700">Blood deficiency or Spleen Qi deficiency</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Headaches triggered by weather or wind</h3>
                  </div>
                  <p className="text-sage-700">External Wind invasion, often affecting the Tai Yang (occipital) area</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Calendar className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-xl font-semibold text-sage-800">Hormonal Headaches</h3>
                  </div>
                  <p className="text-sage-700">Liver Qi stagnation, Blood deficiency, Kidney-Yin deficiency (cycle-linked)</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Holistic Headache & Migraine Support In-Clinic or Online
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* In-Clinic Care */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Acupuncture</h3>
                  </div>
                  <p className="text-sage-700 mb-4">To relieve tension, regulate Qi, and soothe the Liver</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Custom Herbal Medicine</h3>
                  </div>
                  <p className="text-sage-700 mb-4">To address underlying patterns - whether hormonal, emotional, or digestive</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Lifestyle Guidance</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Stress management, dietary advice, and trigger identification</p>
                </CardContent>
              </Card>

              {/* Online Sessions */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <MessageCircle className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Telehealth Consultations</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Headache and migraine guidance from the comfort of your home</p>
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Herbal Support</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Custom formulas mailed to your door for ongoing headache support</p>
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Pain Management Resources</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Acupressure guides, relaxation techniques, and headache prevention tools</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book Your Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              Soften the tension. Clear the fog. Find relief that lasts.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
              onClick={handleBooking}
              disabled={isLoading}
            >
              {isLoading ? 'Booking...' : 'Book Your Session'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default HeadacheMigrainePage; 